/**
 * URL Tracking — the dashboard.
 *
 * Two things on two clocks. The whole picture — chart, breakdowns, recent
 * clicks — is a dozen grouped queries and is fetched once when the screen
 * opens. The counters that tick arrive over a stream, because the server
 * already knows the instant a click is recorded and asking it every few
 * seconds whether anything happened is a worse version of being told. Where
 * the stream cannot be established the hook underneath falls back to polling
 * and says so, which is why the badge reports the mode rather than a state.
 *
 * On the wording: unique visitors is labelled estimated, everywhere, because
 * it is. Someone who clears cookies or moves to another phone counts twice,
 * and there is no honest way to know. Clicks is the firm number — somebody
 * pressed something and this server saw it.
 */
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useApp } from '../../store/AppContext.jsx';
import api from '../../lib/api.js';
import { useTracking, useLiveClicks, download } from '../../lib/urltracking.js';
import Icon from '../../lib/icons.jsx';
import { Card, CardHead, CardBody, EmptyState } from '../../components/Primitives.jsx';
import { TileRow } from '../../components/StatCards.jsx';
import {
  LiveDot, ClicksChart, Breakdown, TopList, TrackState, num, duration, placeOf, ipOf, clickTime, Told
} from './TrackBits.jsx';

export default function TrackDashboard() {
  const { setSheet, can, S } = useApp();
  const navigate = useNavigate();

  const { data, error, loading, reload } = useTracking(() => api.urlTracking.analytics.dashboard());

  /*
   * Live counters sit beside the loaded ones rather than replacing them, so a
   * dropped connection leaves the last good numbers on screen instead of
   * blanking the tiles.
   */
  const [live, setLive] = useState(null);
  const [arrivals, setArrivals] = useState([]);

  const mode = useLiveClicks({
    enabled: !loading && !error,
    onCounters: setLive,
    /*
     * A click that has just landed is inside every one of these windows, so
     * each is bumped by one. This is optimistic on purpose — it makes the
     * number move the instant the click happens — and the snapshot that
     * follows within ten seconds replaces all of it with what the database
     * actually holds. Unique visitors is deliberately not guessed at: whether
     * this visitor is new is a question about every other click, and a single
     * event cannot answer it.
     */
    onClick: click => {
      setLive(prev => {
        const base = prev || {};
        const bump = k => Number(base[k] || 0) + 1;
        return {
          ...base,
          clicks: bump('clicks'),
          clicksToday: bump('clicksToday'),
          clicksThisWeek: bump('clicksThisWeek'),
          clicksThisMonth: bump('clicksThisMonth'),
          clicksLastHour: bump('clicksLastHour'),
          clicksLast5Min: bump('clicksLast5Min')
        };
      });
      setArrivals(list => [click, ...list].slice(0, 8));
    }
  });

  const totals = { ...(data?.totals || {}), ...(live || {}) };
  const nothing = !loading && !error && !totals.campaigns && !totals.links;

  if (nothing) {
    return (
      <Card>
        <CardBody>
          <EmptyState icon="link" tone="g-purple" title="No tracking data yet">
            <p>Create your first campaign to start tracking marketing links.</p>
            {can('utm-campaigns', 'create') ? (
              <button type="button" className="btn primary" onClick={() => setSheet({ kind: 'trackCampaign' })}>
                <Icon name="plus" />Create campaign
              </button>
            ) : null}
          </EmptyState>
        </CardBody>
      </Card>
    );
  }

  return (
    <TrackState loading={loading && !data} error={error}>
      <div className="uthead">
        <LiveDot mode={mode} />
        <ExportButton />
        <button type="button" className="btn" onClick={() => reload(true)}>
          <Icon name="refresh" />Refresh
        </button>
      </div>

      <TileRow
        tiles={[
          ['Total clicks', num(totals.clicks), 'zap', 'g-purple'],
          ['Unique visitors (est.)', num(totals.uniqueVisitors), 'users', 'g-blue'],
          ['Active campaigns', num(totals.activeCampaigns), 'mega', 'g-pink'],
          ['Tracking URLs', num(totals.links), 'link', 'g-cyan',
            () => navigate('/url-tracking/links')],
          ['Clicks today', num(totals.clicksToday), 'cal', 'g-green'],
          ['Last hour', num(totals.clicksLastHour), 'clock', 'g-amber'],
          ['Last 5 min', num(totals.clicksLast5Min), 'wait', 'g-red']
        ]}
      />

      <Card>
        <CardHead icon="chart" tone="g-purple" title="Clicks over time">
          <span className="dim">last 30 days</span>
        </CardHead>
        <CardBody>
          <ClicksChart series={data?.series} />
        </CardBody>
      </Card>

      <div className="two">
        <Breakdown
          title="Sources"
          icon="target"
          tone="g-blue"
          rows={data?.sources}
          empty="No clicks have arrived yet."
        />
        <TopList
          title="Top campaigns"
          icon="mega"
          tone="g-pink"
          rows={data?.topCampaigns}
          onOpen={c => setSheet({ kind: 'trackCampaignView', id: c.id })}
          empty="No campaign has been clicked yet."
        />
      </div>

      <div className="two">
        <Breakdown
          title="Devices"
          icon="grid"
          tone="g-cyan"
          rows={data?.devices}
          empty="No clicks have arrived yet."
        />
        <TopList
          title="Top links"
          icon="link"
          tone="g-green"
          rows={data?.topLinks}
          onOpen={l => setSheet({ kind: 'trackLinkView', id: l.id })}
          empty="No link has been clicked yet."
        />
      </div>

      <Card>
        <CardHead icon="clock" tone="g-amber" title="Recent clicks" count={data?.recent?.length} />
        <CardBody>
          {data?.recent?.length ? (
            <div className="tscroll">
              <table>
                <thead>
                  <tr><th>Time</th><th>Campaign</th><th>Link</th><th>Source</th><th>Location</th><th>IP</th><th>Device</th><th>Time on site</th></tr>
                </thead>
                <tbody>
                  {/* Clicks that arrived over the stream since this table was
                      loaded, marked so it is obvious they are new rather than
                      quietly merged into the loaded rows. */}
                  {arrivals.map((c, i) => (
                    <tr key={`live-${c.at}-${i}`} className="utnew">
                      <td>{clickTime(c.at, S.tz)}</td>
                      <td>{c.campaign || <span className="dim">—</span>}</td>
                      <td>{c.link || <span className="dim">—</span>}</td>
                      <td>{c.source || <span className="dim">—</span>}</td>
                      <td className="dim">—</td>
                      <td className="dim">—</td>
                      <td className="dim">{c.device || 'Unknown'}</td>
                      <td className="dim">—</td>
                    </tr>
                  ))}
                  {data.recent.map((c, i) => (
                    <tr key={`${c.at}-${i}`}>
                      <td>{clickTime(c.at, S.tz)}</td>
                      <td>{c.campaign}</td>
                      <td>{c.link || <span className="dim">—</span>}</td>
                      <td>{c.source || <span className="dim">—</span>}</td>
                      <td><Told of={placeOf(c)} className="dim" /></td>
                      <td><Told of={ipOf(c)} className="utip" /></td>
                      <td className="dim">{c.device}</td>
                      <td>
                        {duration(c.dwellSeconds) || (
                          <span className="dim" title="Only destinations carrying the beacon script can report how long a visit lasted">—</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : <p className="muted">No clicks yet. Share a tracking URL and they will appear here.</p>}
        </CardBody>
      </Card>
    </TrackState>
  );
}

/**
 * Export, offered only to roles the server will actually let export.
 *
 * `can` gates the button and the API gates the answer — asking twice is not
 * redundancy here, it is the difference between a button that returns a 403
 * and one that is simply not there.
 */
function ExportButton() {
  const { can, toast } = useApp();
  const [busy, setBusy] = useState(false);

  if (!can('utm-analytics', 'view')) return null;

  const go = async () => {
    setBusy(true);
    try {
      download(await api.urlTracking.analytics.exportCSV(), 'url-tracking-analytics.csv');
      toast('Analytics exported');
    } catch (e) {
      toast(e.message === 'Forbidden' ? 'Your role cannot export' : e.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <button type="button" className="btn" onClick={go} disabled={busy}>
      <Icon name="down" />{busy ? 'Exporting…' : 'Export CSV'}
    </button>
  );
}
